import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { PUBLIC_QUEUE_API } from '../utils/API'
import { QueueSnapshot } from '../utils/queueStatus'

const POLL_MS = 10000

/**
 * instance แยกจาก axios singleton โดยตั้งใจ
 *
 * utils/axiosSetup.ts ผูก response interceptor ไว้กับ singleton: เจอ 401/403
 * ตอน isLoggedIn=true จะ clearLogInStorage() + reload ทันที
 * หน้าคิวเป็นหน้าสาธารณะที่ลูกค้าซึ่ง login อยู่ก็เปิดได้ — ถ้า endpoint ล่มชั่วคราว
 * แล้วไปโดน interceptor นั้น เท่ากับเตะลูกค้าออกจากระบบเพราะจอคิวเสีย
 *
 * timeout กันเคส "ค้างเฉย ๆ ไม่มีวันตอบ" (สัญญาณอ่อนในร้าน, captive portal ที่
 * รับ connection แต่ไม่ตอบ) — ไม่ตั้งไว้ request ที่ hang จะไม่ reject เลย stale
 * เลยไม่มีวันขึ้น และจอค้างที่ "Loading…" ตลอดไปตั้งแต่โหลดครั้งแรก. 8s < POLL_MS
 * (10s) เพื่อให้ request ที่ค้างจน timeout รอบหนึ่งเสร็จก่อน poll รอบถัดไปเริ่มตาม
 * ปกติ — แต่นี่ไม่ใช่เงื่อนไขความถูกต้อง เพราะ requestId guard ด้านล่างกัน request
 * ที่ยิงซ้อนกัน (ไม่ว่าจะมาจาก timeout ที่ยาวกว่านี้ หรือ visibilitychange refetch
 * ระหว่างที่ request ก่อนหน้ายังค้างอยู่) ไว้แล้วไม่ว่ากรณีไหน
 */
const client = axios.create({ timeout: 8000 })

export const useQueueSnapshot = (): { snapshot: QueueSnapshot | null; stale: boolean } => {
  const [snapshot, setSnapshot] = useState<QueueSnapshot | null>(null)
  const [stale, setStale] = useState(false)
  const timer = useRef<number | undefined>(undefined)
  // นับ generation ของ request — กัน request เก่าที่ค้างนาน (มือถือเน็ตกระตุก) resolve
  // ทีหลัง request ใหม่กว่าแล้วทับข้อมูลสด ๆ ด้วยของเก่า (เลขคิวถอยหลัง)
  const requestId = useRef(0)

  useEffect(() => {
    let cancelled = false

    const fetchOnce = async (): Promise<void> => {
      const id = ++requestId.current
      try {
        const { data } = await client.get(PUBLIC_QUEUE_API)
        // cancelled: unmount แล้ว. id !== requestId.current: มี request ใหม่กว่ายิงไปแล้ว
        // (เช่น จาก interval ถัดไป หรือ visibilitychange refetch) ตัวนี้ค้างมาช้ากว่า ทิ้งได้เลย
        if (cancelled || id !== requestId.current) return
        setSnapshot(data)
        setStale(false)
      } catch {
        // คงตัวเลขเดิมบนจอไว้ ห้ามล้างเป็นค่าว่าง — ลูกค้าที่จ้องจออยู่ไม่ควรเห็นจอวูบ
        if (cancelled || id !== requestId.current) return
        setStale(true)
      }
    }

    const stop = (): void => {
      if (timer.current !== undefined) window.clearInterval(timer.current)
      timer.current = undefined
    }

    const start = (): void => {
      stop()
      fetchOnce()
      timer.current = window.setInterval(fetchOnce, POLL_MS)
    }

    const onVisibility = (): void => {
      if (document.hidden) stop()
      else start()
    }

    start()
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  return { snapshot, stale }
}
